import { Encoder, MGPFallible, MGPOptional, Utils } from '@everyboard/lib';
import { Move } from '../../jscaip/Move';
import { MoveCoordToCoord } from '../../jscaip/MoveCoordToCoord';
import { MoveWithTwoCoords } from '../../jscaip/MoveWithTwoCoords';
import { Ordinal } from '../../jscaip/Ordinal';
import { DiaballikFailure } from './DiaballikFailure';
export class DiaballikBallPass extends MoveCoordToCoord {
    static encoder = MoveWithTwoCoords.getFallibleEncoder(DiaballikBallPass.from);
    static from(start, end) {
        const direction = Ordinal.factory.fromMove(start, end);
        if (direction.isFailure()) {
            return MGPFallible.failure(DiaballikFailure.PASS_MUST_BE_IN_STRAIGHT_LINE());
        }
        return MGPFallible.success(new DiaballikBallPass(start, end));
    }
    constructor(start, end) {
        super(start, end);
    }
    equals(other) {
        return other instanceof DiaballikBallPass && super.equals(other);
    }
}
export class DiaballikTranslation extends MoveCoordToCoord {
    static encoder = MoveWithTwoCoords.getFallibleEncoder(DiaballikTranslation.from);
    static from(start, end) {
        const vector = start.getVectorToward(end);
        if (vector.isSingleOrthogonalStep()) {
            return MGPFallible.success(new DiaballikTranslation(start, end));
        }
        else {
            return MGPFallible.failure(DiaballikFailure.MUST_MOVE_BY_ONE_ORTHOGONAL_SPACE());
        }
    }
    constructor(start, end) {
        super(start, end);
    }
    equals(other) {
        return other instanceof DiaballikTranslation && super.equals(other);
    }
}
export function isTranslation(subMove) {
    return subMove instanceof DiaballikTranslation;
}
export function isBallPass(subMove) {
    return subMove instanceof DiaballikBallPass;
}
export class DiaballikMove extends Move {
    first;
    second;
    third;
    static subMoveEncoder = Encoder.disjunction([isTranslation, isBallPass], [DiaballikTranslation.encoder, DiaballikBallPass.encoder]);
    static subMoveOptionalEncoder = MGPOptional.getEncoder(DiaballikMove.subMoveEncoder);
    static encoder = Encoder.tuple([DiaballikMove.subMoveEncoder, DiaballikMove.subMoveOptionalEncoder, DiaballikMove.subMoveOptionalEncoder], (move) => [move.first, move.second, move.third], (fields) => {
        return new DiaballikMove(fields[0], fields[1], fields[2]);
    });
    static countPassesAndTranslations(subMove, passesAndTranslations) {
        if (subMove instanceof DiaballikBallPass) {
            passesAndTranslations.passes++;
        }
        else {
            passesAndTranslations.translations++;
        }
    }
    constructor(first, second, third) {
        super();
        this.first = first;
        this.second = second;
        this.third = third;
        if (third.isPresent()) {
            Utils.assert(second.isPresent(), 'DiaballikMove should have two first actions to have a third one');
        }
        const passesAndTranslations = {
            passes: 0,
            translations: 0,
        };
        DiaballikMove.countPassesAndTranslations(first, passesAndTranslations);
        if (second.isPresent()) {
            DiaballikMove.countPassesAndTranslations(second.get(), passesAndTranslations);
        }
        if (third.isPresent()) {
            DiaballikMove.countPassesAndTranslations(third.get(), passesAndTranslations);
        }
        Utils.assert(passesAndTranslations.passes <= 1, 'DiaballikMove should have at most one pass');
        Utils.assert(passesAndTranslations.translations <= 2, 'DiaballikMove should have at most two translations');
    }
    toString() {
        return `DiaballikMove(${this.first.toString()}, ${this.second.toString()}, ${this.third.toString()})`;
    }
    equals(other) {
        if (this.first.equals(other.first) === false)
            return false;
        if (this.second.equals(other.second) === false)
            return false;
        if (this.third.equals(other.third) === false)
            return false;
        return true;
    }
    getSubMoves() {
        const subMoves = [this.first];
        if (this.second.isPresent()) {
            subMoves.push(this.second.get());
        }
        if (this.third.isPresent()) {
            subMoves.push(this.third.get());
        }
        return subMoves;
    }
}
